import React from 'react';

import {CheckBox} from '@/components';
import type {AfriexCurrency} from '@/types/Currency';

export type CorridorPermission =
  | 'canDeposit'
  | 'canWithdraw'
  | 'canTransferOutward'
  | 'canTransferInward'
  | 'canSwapOutward'
  | 'canSwapInward'
  | 'canRefer';

interface CorridorKillSwitchesProps {
  permissions: AfriexCurrency['permissions'];
  onToggle: (permission: CorridorPermission, allowed: boolean) => void;
}

/**
 * The corridor kill switches, replacing the `block*` Parameter Store keys.
 *
 * Phrased as "allowed", not "blocked", because that is what the record stores:
 * `blockWithdraw = true` became `canWithdraw = false`. Showing the stored sense
 * avoids a second inversion in the reader's head at exactly the moment they are
 * closing a corridor under pressure.
 *
 * Swap is TWO switches rather than one. A single `canSwap` would close both
 * directions together, which traps balances: when a currency's liquidity goes
 * you want to stop people acquiring it while still letting holders convert
 * out. It replaces the old `canSwap`, which was left off this screen for years
 * because nothing enforced it.
 */
const SWITCHES: Array<{
  key: CorridorPermission;
  label: string;
  hint: string;
}> = [
  {
    key: 'canDeposit',
    label: 'Deposits allowed',
    hint: 'Turning this off blocks new deposits from this currency.',
  },
  {
    key: 'canWithdraw',
    label: 'Withdrawals allowed',
    hint: 'Also blocks the destination leg of an end-to-end deposit into this currency.',
  },
  {
    key: 'canTransferOutward',
    label: 'In-app transfers out allowed',
    hint: 'Blocks sending from a wallet in this currency.',
  },
  {
    key: 'canTransferInward',
    label: 'In-app transfers in allowed',
    hint: 'Blocks receiving into a wallet in this currency.',
  },
  {
    key: 'canSwapOutward',
    label: 'Swaps out allowed',
    hint: 'Blocks swapping FROM this currency. Turning this off strands anyone holding it, so prefer closing inward first.',
  },
  {
    key: 'canSwapInward',
    label: 'Swaps in allowed',
    hint: 'Blocks swapping INTO this currency. The usual lever when liquidity dries up: holders can still convert out.',
  },
  {
    key: 'canRefer',
    label: 'Referral payouts allowed',
    hint: "Keyed on the sender's currency, resolved from their country.",
  },
];

const CorridorKillSwitches: React.FC<CorridorKillSwitchesProps> = ({
  permissions,
  onToggle,
}) => (
  <div className="space-y-3 mb-4">
    <p className="text-xs text-slate-500 dark:text-slate-400">
      These reject the transaction on the server, so they are the hard stop.
      Unlike Availability on the Countries tab, which only hides options in the
      app, a corridor closed here is closed for old app builds and direct API
      calls too.
    </p>

    <p className="text-xs text-slate-500 dark:text-slate-400">
      Unset counts as allowed, so a switch that has never been touched leaves
      the corridor open.
    </p>

    {SWITCHES.map(({key, label, hint}) => {
      const value = permissions?.[key];
      return (
        <div key={key}>
          <CheckBox
            id={key}
            label={label}
            // Only an explicit false blocks, matching the server: undefined
            // and null both mean allowed.
            checked={value !== false}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onToggle(key, e.target.checked)
            }
          />
          <p className="ml-8 -mt-2 text-xs text-slate-500 dark:text-slate-400">
            {hint}
            {value === undefined ? ' Currently unset (allowed).' : ''}
          </p>
        </div>
      );
    })}
  </div>
);

export default CorridorKillSwitches;
